import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import IconButton from '@material-ui/core/IconButton'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction'
import ListItemText from '@material-ui/core/ListItemText'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import AddIcon from '@material-ui/icons/Add'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'

import {
  createUserDefinedPrompt,
  deleteUserDefinedPrompt,
  getDefaultWipClientSummarySystemPrompt,
  myUserDefinedPrompts,
  updateUserDefinedPrompt,
} from '../../../entities/userDefinedPrompts/service'

const PURPOSE_WIP_CLIENT_SUMMARY = 'WIP_CLIENT_SUMMARY'

const useStyles = makeStyles(theme => ({
  layout: {
    display: 'flex',
    gap: theme.spacing(2),
    minHeight: 360,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  listPane: {
    width: 260,
    flexShrink: 0,
    borderRight: '1px solid #e0e0e0',
    paddingRight: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      borderRight: 'none',
      borderBottom: '1px solid #e0e0e0',
      paddingBottom: theme.spacing(1),
    },
  },
  editorPane: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  listHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
}))

const WipSystemPromptManager = props => {
  const classes = useStyles()
  const { open, onClose, client, selectedPrompt, onSelectPrompt } = props

  const [loading, setLoading] = useState(false)
  const [defaultContent, setDefaultContent] = useState('')
  const [userPrompts, setUserPrompts] = useState([])
  /** 'default' | 'new' | mongo id */
  const [listSelection, setListSelection] = useState('default')
  const [editName, setEditName] = useState('default')
  const [editContent, setEditContent] = useState('')

  const loadPrompts = useCallback(async () => {
    if (!client) return { defText: '', list: [] }
    const [defText, list] = await Promise.all([
      getDefaultWipClientSummarySystemPrompt(client),
      myUserDefinedPrompts(client, PURPOSE_WIP_CLIENT_SUMMARY),
    ])
    setDefaultContent(defText)
    setUserPrompts(list)
    return { defText, list }
  }, [client])

  useEffect(() => {
    if (!open || !client) return undefined
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const { defText, list } = await loadPrompts()
        if (cancelled) return
        const initialId =
          selectedPrompt?.id &&
          selectedPrompt.id !== 'default' &&
          list.some(p => p.id === selectedPrompt.id)
            ? selectedPrompt.id
            : 'default'
        setListSelection(initialId)
        if (initialId === 'default') {
          setEditName('default')
          setEditContent(defText)
        } else {
          const row = list.find(p => p.id === initialId)
          setEditName(row?.name ?? '')
          setEditContent(row?.content ?? '')
        }
      } catch (e) {
        console.error(e)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, client, loadPrompts, selectedPrompt?.id])

  useEffect(() => {
    if (listSelection === 'default' && defaultContent) {
      setEditContent(defaultContent)
    }
  }, [defaultContent, listSelection])

  const handleListClick = sel => {
    setListSelection(sel)
    if (sel === 'default') {
      setEditName('default')
      setEditContent(defaultContent)
      return
    }
    if (sel === 'new') {
      setEditName('New prompt')
      setEditContent('')
      return
    }
    const row = userPrompts.find(p => p.id === sel)
    setEditName(row?.name ?? '')
    setEditContent(row?.content ?? '')
  }

  const handleAddDraft = () => {
    setListSelection('new')
    setEditName('New prompt')
    setEditContent('')
  }

  const handleSave = async () => {
    if (!client || listSelection === 'default') return
    setLoading(true)
    try {
      if (listSelection === 'new') {
        const created = await createUserDefinedPrompt(client, {
          name: editName.trim() || 'Untitled',
          content: editContent,
          purpose: PURPOSE_WIP_CLIENT_SUMMARY,
        })
        await loadPrompts()
        setListSelection(created.id)
        setEditName(created.name)
        setEditContent(created.content)
      } else {
        await updateUserDefinedPrompt(client, {
          id: listSelection,
          name: editName.trim(),
          content: editContent,
        })
        await loadPrompts()
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!client || listSelection === 'default' || listSelection === 'new') return
    if (!window.confirm('Delete this system prompt?')) return
    setLoading(true)
    try {
      await deleteUserDefinedPrompt(client, listSelection)
      const { defText } = await loadPrompts()
      setListSelection('default')
      setEditName('default')
      setEditContent(defText)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleUsePrompt = () => {
    if (listSelection === 'new') return
    if (listSelection === 'default') {
      onSelectPrompt({ id: 'default', name: 'default', content: defaultContent })
    } else {
      const row = userPrompts.find(p => p.id === listSelection)
      if (row) {
        onSelectPrompt({ id: row.id, name: row.name, content: row.content })
      }
    }
    onClose()
  }

  const readOnly = listSelection === 'default'
  const isNew = listSelection === 'new'
  const canSave = !readOnly && (isNew ? Boolean(editName.trim() || editContent.trim()) : true)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>LLM instructions (system prompt)</DialogTitle>
      <DialogContent>
        {loading && !defaultContent ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
            <CircularProgress />
          </div>
        ) : (
          <div className={classes.layout}>
            <div className={classes.listPane}>
              <div className={classes.listHeader}>
                <Typography variant="subtitle2">Prompts</Typography>
                <IconButton size="small" aria-label="Add prompt" onClick={handleAddDraft}>
                  <AddIcon />
                </IconButton>
              </div>
              <List dense component="nav">
                <ListItem
                  button
                  selected={listSelection === 'default'}
                  onClick={() => handleListClick('default')}
                >
                  <ListItemText primary="default" secondary="Built-in (read-only)" />
                </ListItem>
                {isNew && (
                  <ListItem
                    button
                    selected={listSelection === 'new'}
                    onClick={() => handleListClick('new')}
                  >
                    <ListItemText primary="New prompt (unsaved)" />
                  </ListItem>
                )}
                {userPrompts.map(row => (
                  <ListItem
                    key={row.id}
                    button
                    selected={listSelection === row.id}
                    onClick={() => handleListClick(row.id)}
                  >
                    <ListItemText primary={row.name || 'Untitled'} />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        aria-label="Delete"
                        onClick={e => {
                          e.stopPropagation()
                          ;(async () => {
                            if (!window.confirm('Delete this system prompt?')) return
                            setLoading(true)
                            try {
                              await deleteUserDefinedPrompt(client, row.id)
                              const { defText } = await loadPrompts()
                              setListSelection('default')
                              setEditName('default')
                              setEditContent(defText)
                            } catch (err) {
                              console.error(err)
                            } finally {
                              setLoading(false)
                            }
                          })()
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </div>
            <div className={classes.editorPane}>
              <TextField
                label="Name"
                fullWidth
                variant="outlined"
                value={editName}
                disabled={readOnly || loading}
                onChange={e => setEditName(e.target.value)}
              />
              <TextField
                label="System prompt"
                fullWidth
                multiline
                minRows={12}
                variant="outlined"
                value={editContent}
                disabled={readOnly || loading}
                onChange={e => setEditContent(e.target.value)}
              />
              <Typography variant="caption" color="textSecondary">
                The built-in &quot;default&quot; prompt cannot be edited. Add a new prompt with + to
                customize.
              </Typography>
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {!readOnly && (
          <Button onClick={handleSave} color="primary" disabled={loading || !canSave}>
            Save
          </Button>
        )}
        {!readOnly && !isNew && (
          <Button onClick={handleDelete} disabled={loading}>
            Delete
          </Button>
        )}
        <Button
          onClick={handleUsePrompt}
          color="primary"
          variant="contained"
          disabled={loading || isNew}
        >
          Use this prompt
        </Button>
      </DialogActions>
    </Dialog>
  )
}

WipSystemPromptManager.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  client: PropTypes.object,
  selectedPrompt: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  onSelectPrompt: PropTypes.func.isRequired,
}

WipSystemPromptManager.defaultProps = {
  client: null,
  selectedPrompt: { id: 'default', name: 'default' },
}

export default WipSystemPromptManager
